import { ExternalLink } from "lucide-react";

export function AboutRoles() {
    return (
        <div className="space-y-8">
            <div className="space-y-4">
                <h3 className="text-xl font-bold">The Goal: True Self-Organization</h3>
                <p className="text-muted-foreground leading-relaxed">
                    Many organizations introduce these leadership roles through external Agile Coaches or Scrum Masters.
                    However, true self-organization is only achieved when these roles are <strong>transferred into the team</strong> itself.
                </p>

                <div className="border rounded-xl overflow-hidden bg-background my-6 shadow-sm">
                    <img
                        src="/images/role_transfer_diagram_dark_v2.png"
                        alt="Diagram showing the transition from a Coach-led team to a Self-Organizing team where members hold the leadership roles."
                        className="w-full h-auto object-cover"
                    />
                    <div className="p-3 bg-muted/30 text-xs text-center text-muted-foreground border-t">
                        Visualization of the role transfer process from Agile Coach to Team ownership.
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/50">
                        <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2">Phase 1: Coach-Led</h4>
                        <p className="text-sm text-foreground/80">
                            Roles are centralized in an Agile Coach or Scrum Master. The team relies on this person for structure, protection, and connections.
                        </p>
                    </div>
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-100 dark:border-green-900/50">
                        <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2">Phase 2: Team-Led</h4>
                        <p className="text-sm text-foreground/80">
                            Roles are distributed among team members. The team facilitates itself, manages its own boundaries, and networks independently.
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold">Scientific Foundation</h3>
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 mb-6">
                    <h4 className="font-medium text-primary mb-2">Connects with Daniel Pink's Drive</h4>
                    <p className="text-sm text-muted-foreground">
                        This framework manifests <strong>Autonomy, Mastery, and Purpose</strong> into the organization to achieve <strong>FLOW</strong>.
                    </p>
                    <ul className="mt-2 text-sm text-muted-foreground list-disc pl-4 space-y-1">
                        <li><strong>Autonomy (FA):</strong> Facilitator Autonomy roles Enable decision making</li>
                        <li><strong>Mastery (TA):</strong> Technical/Team Autonomy roles develop capability</li>
                        <li><strong>Purpose (PO):</strong> Product Owner provides direction (e.g. via OKRs)</li>
                    </ul>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                    Based on academic research by <span className="font-medium text-foreground">Spiegler, S. V., Heinecke, C., & Wagner, S. (2019)</span>:
                    <span className="italic"> "Leadership Gap in Agile Teams: How Teams and Scrum Masters Mature."</span>
                </p>
            </div>

            <div className="rounded-lg bg-muted p-4">
                <div className="flex items-center gap-2 font-medium mb-1">
                    <ExternalLink className="h-4 w-4" />
                    Key Insight
                </div>
                <p className="text-sm text-muted-foreground">
                    "The goal is to transfer those roles into the team so that it can self-organize."
                </p>
            </div>

            <div className="space-y-6 pt-4 border-t">
                <h3 className="text-lg font-semibold">The Step-by-Step Process</h3>
                <div className="relative border-l-2 border-muted pl-8 space-y-8 ml-4">
                    {[
                        "Demonstrate role (Facilitator models behavior)",
                        "Observe role (Team watches & learns)",
                        "Leadership gap (Facilitator steps back)",
                        "Claim and grant role (Team ownership)",
                        "Play role (Team executes)",
                        "Support if needed (Facilitator backup)"
                    ].map((step, index) => (
                        <div key={index} className="relative">
                            <span className="absolute -left-[41px] top-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                                {index + 1}
                            </span>
                            <h4 className="font-medium text-foreground">{step}</h4>
                        </div>
                    ))}
                </div>

                <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-xl p-6 flex gap-4">
                    <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-500 shrink-0" />
                    <div className="space-y-2">
                        <h4 className="font-semibold text-orange-700 dark:text-orange-400">Critical Success Factor: The Leadership Gap</h4>
                        <p className="text-sm text-foreground/80">
                            Becoming a truly agile team requires time! You must intentionally create a <strong>Leadership Gap</strong> where the coach steps back.
                            This uncomfortable vacuum is the only space where the team can step up and claim new responsibilities.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

import { AlertTriangle } from "lucide-react";
